import type { RouteLocation } from 'vue-router';
import type { RouteNamedMapI18n } from 'vue-router/auto-routes';

/**
 * Returns the route name without any i18n suffixes
 */
export function getRouteName(route: RouteLocation): keyof RouteNamedMapI18n {
    return (route?.name as string)?.split('___')?.[0] as keyof RouteNamedMapI18n ?? route?.name;
}

/**
 * Returns the product key of the route
 */
export function getProductKeyFromRoute(route: RouteLocation | undefined): ProductKey | undefined {
    return route
        ?.path
        ?.split('/')
        .find(seg => Object.values(PRODUCT_KEYS)
            .includes(seg as ProductKey)) as ProductKey | undefined;
}

/**
 * Returns the product ID of the route
 */
export function getProductIdFromRoute(route: RouteLocation | undefined): ProductId | undefined {
    const productKey = getProductKeyFromRoute(route);
    const productId = typeof route?.params?.productRouteId === 'string' && !Number.isNaN(Number(route.params.productRouteId)) ? Number.parseInt(route.params.productRouteId) : undefined;
    return productKey && productId ? `${productKey}-${productId}` as ProductId : undefined;
}

/**
 * Returns the product config of the route (based on product key)
 */
export function getProductConfigFromRoute(route: RouteLocation) {
    const key = getProductKeyFromRoute(route);
    return productConfigs.find(c => c.key === key) ?? undefined;
}

/**
 *
 */
export function getProductRouteId(productId: ProductId): ProductRouteId | undefined {
    const parts = productId.split('-');
    const idPart = parts[parts.length - 1];
    return idPart !== undefined ? Number(idPart) as ProductRouteId : undefined;
}

/**
 *
 */
export function getProductKeyFromId(productId: ProductId): ProductKey | undefined {
    const parts = productId.split('-');
    const keyPart = parts.slice(0, -1).join('-');
    return keyPart !== undefined ? keyPart as ProductKey : undefined;
}

/**
 * Returns the flow type of the route (booking / upsell)
 */
export function getFlowTypeFromRoute(route: RouteLocation) {
    return route.params?.flowType as FlowType | undefined;
}

/**
 * Navigates to last step.
 *
 * If the last steps equals the last step in browser history, go back in history.
 * Otherwise, navigate to the given route.
 *
 * Why?
 *
 * If you navigate "normally" back and forth, this makes sure the back button does the same thing
 * as the browser back button and your history stay clean.
 * (This prevents browser history to be: step1 -> step2 -> step1 -> step2 ...)
 *
 * You only want this if your previous browser step actually was the previous step in the flow.
 * For example if you go back to a page from the cart (editing), you don't want to go back in history,
 * but to the step directly.
 *
 * Step 1 -> Step 2 -> Step 1
 *                     (use history back)
 *
 * Step 3 -> Cart   -> Step 1
 *                     (go to step 1)
 *
 *
 */
export function navigateBack(route: RouteLocation | undefined) {
    if (!route) {
        return;
    }
    if (route.fullPath === history.state.back) {
        return useRouter().back();
    }
    else {
        return navigateTo(route);
    }
}
